RivalIQ.apps.SocialPosts.module('data', function (data, App, Backbone, Marionette, $, _, s) {

    'use strict';

    function sortOptional(cacheValue, fetchValue, attributes) {
        const needsServerSideSort = App.utils.dimensionsNeedServerSideSort(attributes.dimensions);
        return (!needsServerSideSort) || !_.exists(fetchValue) || _.isEqual(cacheValue, fetchValue);
    }

    function optional(cacheValue, fetchValue) {
        return cacheValue === fetchValue || (!cacheValue && !fetchValue);
    }

    function boolOptional(cacheValue, fetchValue) {
        return cacheValue === fetchValue || _.isUndefined(cacheValue) || _.isUndefined(fetchValue);
    }

    const serializers = {
        companyIds: function (val) {

            return (_.isArray(val) ? val.join(',') : val);
        }
    };

    // NOTE the `dimensions` herein effectively turn "on" more sophisticated
    // cache checking for `limit`
    const dimensionsWithLimits = [
        'post',
        'hashtag',
        'primaryDomain'
    ];

    const SocialPostsCacheLayer = RivalIQ.data.CacheLayer.extend({
        cacheKeys: [
            RivalIQ.data.CacheLayer.keyType.TIMEPERIOD,
            'channel',
            'landscapeId',
            'focusCompanyId',
            'withObservations',
            {
                value: 'companyIds',
                test: function (cacheValue, fetchValue, attributes) {

                    const serializer = serializers.companyIds;
                    return (optional(serializer(cacheValue), serializer(fetchValue), attributes) || (attributes.domainsOnly && !cacheValue && fetchValue));
                }
            },
            'startDate',
            'endDate',
            'companySetDescriptor',
            'domainsOnly',
            'excludePostsWithoutLinks',
            'hashtagsMinPosts',
            'hashtagsMinCompanies',
            'keywordsOnly',
            'keywordsMinPosts',
            'keywordsMinCompanies',
            'keywordsMinWords',
            'keywordsScoreRankCutoff',
            'postIds',
            'includeDimensionResults',
            'canonicalUrlExactMatch',
            {
                value: 'includePosts',
                test: boolOptional
            },
            {
                value: 'includeBreakdowns',
                test: boolOptional
            },
            {
                value: 'type',
                test: optional
            },
            {
                value: 'tweetType',
                test: optional
            },
            {
                value: 'promotedPosts',
                test: optional
            },
            {
                value: 'postTagIds',
                test: optional
            },
            {
                value: 'postTagConjunction',
                test: optional
            },
            {
                value: 'postTagExclude',
                test: optional
            },
            {
                value: 'mustHavePostTagIds',
                test: optional
            },
            {
                value: 'domainModeFilter',
                test: optional
            },
            'limitPostTagIds',
            'includeSummaryRow',
            'timezone',
            {
                value: 'sortOrder',
                test: sortOptional
            },
            {
                value: 'orderBy',
                test: sortOptional
            },
            {
                value: 'limit',
                test: function (cacheValue, fetchValue, attributes) {
                    const hasDimensionWithLimit = _.some(dimensionsWithLimits, function (dimension) {
                        return _.includes(attributes.dimensions, dimension);
                    });

                    return (!hasDimensionWithLimit && !attributes.domainsOnly) ||
                        !_.exists(fetchValue) ||
                        cacheValue === -1 ||
                        (cacheValue >= fetchValue && fetchValue !== -1);
                }
            },
            {
                value: 'engagementFloor',
                test: sortOptional
            },
            {
                value: 'metricValues',
                test: sortOptional
            },
            {
                value: 'minimumDesiredResults',
                test: sortOptional
            },
            'postAutoTagId'
        ].concat(_.keys(RivalIQ.enums.searchFieldNames)),

        loadData: function (attributes) {
            const me = this;

            const collection = new me.Collection();

            attributes.reset = true;
            const pathParams = ['landscapeId', 'channel', 'withObservations'];
            _.extend(collection, _.pick(attributes, pathParams));

            const requestData = _.pick(attributes, App.utils.validQueryParams);

            return collection.fetch({
                data: requestData,
                processData: true
            });
        },

        fetch: function (params) {
            const me = this;

            params = App.collections.Posts.normalizeParams(params);
            const def = me.getOrLoad(params);
            return def;
        }
    });


    function dimensionSortOptional(cacheValue, fetchValue) {
        return !_.exists(fetchValue) || _.isEqual(cacheValue, fetchValue);
    }

    const PostedUrlsCacheLayer = RivalIQ.data.BatchCacheLayer.extend({

        Collection: App.collections.PostedUrls,

        cacheKeys: [
            RivalIQ.data.CacheLayer.keyType.TIMEPERIOD,
            'channel',
            'landscapeId',
            'focusCompanyId',
            'withObservations',
            {
                value: 'companyIds',
                test: function (cacheValue, fetchValue, attributes) {

                    const serializer = serializers.companyIds;
                    return (optional(serializer(cacheValue), serializer(fetchValue), attributes) || (attributes.domainsOnly && !cacheValue && fetchValue));
                }
            },
            'startDate',
            'endDate',
            'companySetDescriptor',
            'domainsOnly',
            'excludePostsWithoutLinks',
            'keywordsOnly',
            'includeDimensionResults',
            'canonicalUrlExactMatch',
            {
                value: 'includeObservations',
                test: optional
            },
            {
                value: 'type',
                test: optional
            },
            {
                value: 'tweetType',
                test: optional
            },
            {
                value: 'promotedPosts',
                test: optional
            },
            {
                value: 'postTagIds',
                test: optional
            },
            {
                value: 'domainModeFilter',
                test: optional
            },
            'timezone',

            // properties for returning companyId expansions
            {
                value: 'companyIdCanonicalUrlLimit',
                test: dimensionSortOptional
            },
            {
                value: 'companyIdCanonicalUrlOrderBy',
                test: dimensionSortOptional
            },
            {
                value: 'companyIdCanonicalUrlSortOrder',
                test: dimensionSortOptional
            },
            {
                value: 'companyIdPrimaryDomainLimit',
                test: dimensionSortOptional
            },
            {
                value: 'companyIdPrimaryDomainOrderBy',
                test: dimensionSortOptional
            },
            {
                value: 'companyIdPrimaryDomainSortOrder',
                test: dimensionSortOptional
            },

            // other top level free dimensions
            {
                value: 'primaryDomainLimit',
                test: dimensionSortOptional
            },
            {
                value: 'primaryDomainOrderBy',
                test: dimensionSortOptional
            },
            {
                value: 'primaryDomainSortOrder',
                test: dimensionSortOptional
            },
            {
                value: 'canonicalUrlLimit',
                test: dimensionSortOptional
            },
            {
                value: 'canonicalUrlOrderBy',
                test: dimensionSortOptional
            },
            {
                value: 'canonicalUrlSortOrder',
                test: dimensionSortOptional
            }

        ].concat(_.keys(RivalIQ.enums.searchFieldNames)),

        loadData: function (requests) {
            const me = this;

            const collection = new me.Collection();

            const attributes = _.reduce(requests, function (memo, request) {
                return _.merge(memo, request);
            }, {
                reset: true
            });

            const pathParams = ['landscapeId', 'channel', 'withObservations'];
            _.extend(collection, _.pick(attributes, pathParams));

            return collection.fetch({
                data: _.pick(attributes, App.utils.validPostedUrlQueryParams),
                processData: true
            }).then(function (result) {
                return _.times(requests.length, _.constant(result));
            });
        },

        fetch: function (params) {
            const me = this;

            params = App.collections.Posts.normalizePostedUrlsParams(params);
            const def = me.getOrLoad(params);
            return def;
        }
    });

    const cacheLayers = [
        {
            name: 'SocialPosts',
            Collection: App.collections.Posts
        },
        {
            name: 'Hashtags',
            Collection: App.collections.Hashtags
        },
        {
            name: 'PostTagIds',
            Collection: App.collections.PostTagIds
        }
    ];

    _.each(cacheLayers, function (cacheLayer) {
        const CacheLayer = SocialPostsCacheLayer.extend(cacheLayer);
        _.set(data, s.decapitalize(cacheLayer.name), new CacheLayer());
    });

    data.postedUrls = new PostedUrlsCacheLayer();

    data.invalidateAllSocialPostsCaches = function (reissueInflightRequests) {
        _.each(cacheLayers, function (cacheLayer) {
            data[s.decapitalize(cacheLayer.name)].invalidateCaches(reissueInflightRequests);
        });

        RivalIQ.metrics.new.client.CacheLayer.invalidateCaches(reissueInflightRequests);
    };
}, window.s);
